import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Moon,
  Thermometer,
  HeartPulse,
  Sparkles,
  ShieldCheck,
  FileText,
  Lock,
  Eye,
  ArrowRight,
  Dumbbell,
  Gauge,
} from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";

import heroLgAvif from "@/assets/hero-lg.avif.asset.json";
import heroLgWebp from "@/assets/hero-lg.webp.asset.json";
import heroLgJpg from "@/assets/hero-lg.jpg.asset.json";
import heroSmAvif from "@/assets/hero-sm.avif.asset.json";
import heroSmWebp from "@/assets/hero-sm.webp.asset.json";
import heroSmJpg from "@/assets/hero-sm.jpg.asset.json";
import periAvif480 from "@/assets/stages/peri-480.avif.asset.json";
import periAvif800 from "@/assets/stages/peri-800.avif.asset.json";
import periWebp480 from "@/assets/stages/peri-480.webp.asset.json";
import periWebp800 from "@/assets/stages/peri-800.webp.asset.json";
import periJpg480 from "@/assets/stages/peri-480.jpg.asset.json";
import periJpg800 from "@/assets/stages/peri-800.jpg.asset.json";
import menoAvif480 from "@/assets/stages/meno-480.avif.asset.json";
import menoAvif800 from "@/assets/stages/meno-800.avif.asset.json";
import menoWebp480 from "@/assets/stages/meno-480.webp.asset.json";
import menoWebp800 from "@/assets/stages/meno-800.webp.asset.json";
import menoJpg480 from "@/assets/stages/meno-480.jpg.asset.json";
import menoJpg800 from "@/assets/stages/meno-800.jpg.asset.json";
import postAvif480 from "@/assets/stages/post-480.avif.asset.json";
import postAvif800 from "@/assets/stages/post-800.avif.asset.json";
import postWebp480 from "@/assets/stages/post-480.webp.asset.json";
import postWebp800 from "@/assets/stages/post-800.webp.asset.json";
import postJpg480 from "@/assets/stages/post-480.jpg.asset.json";
import postJpg800 from "@/assets/stages/post-800.jpg.asset.json";

const STAGE_SIZES = "(max-width: 640px) 92vw, (max-width: 1024px) 45vw, 360px";
const stageSources = {
  peri: {
    avif: `${periAvif480.url} 480w, ${periAvif800.url} 800w`,
    webp: `${periWebp480.url} 480w, ${periWebp800.url} 800w`,
    jpg: `${periJpg480.url} 480w, ${periJpg800.url} 800w`,
    fallback: periJpg800.url,
  },
  meno: {
    avif: `${menoAvif480.url} 480w, ${menoAvif800.url} 800w`,
    webp: `${menoWebp480.url} 480w, ${menoWebp800.url} 800w`,
    jpg: `${menoJpg480.url} 480w, ${menoJpg800.url} 800w`,
    fallback: menoJpg800.url,
  },
  post: {
    avif: `${postAvif480.url} 480w, ${postAvif800.url} 800w`,
    webp: `${postWebp480.url} 480w, ${postWebp800.url} 800w`,
    jpg: `${postJpg480.url} 480w, ${postJpg800.url} 800w`,
    fallback: postJpg800.url,
  },
} as const;

import { captureRefParam } from "@/lib/refParam";
import { capturePromoParam } from "@/lib/promoParam";


const HERO_SIZES = "(max-width: 640px) 92vw, 540px";
const HERO_AVIF_SRCSET = `${heroSmAvif.url} 720w, ${heroLgAvif.url} 1280w`;
const HERO_WEBP_SRCSET = `${heroSmWebp.url} 720w, ${heroLgWebp.url} 1280w`;
const HERO_JPG_SRCSET = `${heroSmJpg.url} 720w, ${heroLgJpg.url} 1280w`;
const HERO_JPG_FALLBACK = heroLgJpg.url;

// KnowMeno brand palette — mapped onto the editorial layout.
const TOKENS = {
  ivory: "#FBF7F4",       // page background (cream)
  sand: "#FFE3E8",        // alt section bg (blush-soft)
  taupe: "#C8B8D4",       // body text on dark plum
  charcoal: "#1A0A24",    // primary text / headings (darker ink for AA+ contrast)
  softBlack: "#1C0828",   // deep section + footer (plum-deep)
  sage: "#FF8A8E",        // positive accent (coral-soft)
  clay: "#F34E54",        // italic display accent (coral)
  blush: "#FFC1CD",       // soft accent
  white: "#FFFFFF",
  data: "#3F2F4A",        // small labels (darker for contrast)
  muted: "#3F2F4A",       // body text on cream (darker for contrast)
  plum: "#2D0E43",        // CTAs / pill buttons
};


const SERIF = 'var(--font-display)'; // Gloock — heavier display serif
const SERIF_ED = "var(--font-serif)"; // Instrument Serif — italic accent
const UI = "var(--font-ui)";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "KnowMeno — Your menopause data, finally understood" },
      {
        name: "description",
        content:
          "KnowMeno turns symptoms and daily patterns into clear insights women can understand — and doctors can actually use.",
      },
      { property: "og:title", content: "KnowMeno — Your menopause data, finally understood" },
      {
        property: "og:description",
        content:
          "Daily tracking and a doctor-ready Symptom Intelligence Report.",
      },
      { property: "og:url", content: "https://knowmeno.com/" },
      { property: "og:image", content: "https://knowmeno.com/og-image.png?v=2" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "KnowMeno — Your menopause data, finally understood" },
      {
        name: "twitter:description",
        content:
          "Daily tracking and a doctor-ready Symptom Intelligence Report.",
      },
      { name: "twitter:image", content: "https://knowmeno.com/og-image.png?v=2" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { name: "theme-color", content: "#F6F1E8" },
    ],
    links: [
      {
        rel: "preload",
        as: "image",
        href: heroSmAvif.url,
        type: "image/avif",
        imageSrcSet: HERO_AVIF_SRCSET,
        imageSizes: HERO_SIZES,
        fetchPriority: "high",
      } as any,
      { rel: "canonical", href: "https://knowmeno.com/" },
      { rel: "alternate", hrefLang: "en", href: "https://knowmeno.com/" },
      { rel: "alternate", hrefLang: "es", href: "https://sepameno.com/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://knowmeno.com/" },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  useEffect(() => {
    captureRefParam();
    capturePromoParam();
  }, []);

  return (
    <div
      style={{
        background: TOKENS.ivory,
        color: TOKENS.charcoal,
        fontFamily: UI,
        // Pull content up under the transparent fixed header for cinematic hero.
        marginTop: "calc(-1 * (var(--km-header-h, 72px)))",
      }}
    >
      <style>{`
        :root { --km-header-h: 72px; }
        @media (max-width: 640px) { :root { --km-header-h: 64px; } }
        /* Bolder, higher-contrast marketing typography */
        body { font-weight: 600; color: ${TOKENS.charcoal}; -webkit-font-smoothing: antialiased; }
        h1, h2, h3, h4 { font-weight: 600; letter-spacing: -0.02em; }
        p { font-weight: 500; }
      `}</style>

      <Hero />
      <OutlookSection />
      <NinetyDayJourney />
      <PostmenoSection />
      <StagesSection />
      <PatternsSection />
      <DoctorSection />
      <WearablesSection />
      <TrustSection />
      <FinalCTA />
    </div>
  );
}

/* ---------------- Hero ---------------- */

function useSkinTempTicker() {
  // Cycle a small set of demo readings so the hero tile visibly ticks
  // up and down. Pure presentation — no wearable data wired.
  const readings = [0.2, 0.3, 0.4, 0.5, 0.4, 0.3];
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % readings.length), 1800);
    return () => clearInterval(t);
  }, [readings.length]);
  const curr = readings[i];
  const prev = readings[(i - 1 + readings.length) % readings.length];
  const dir = curr > prev ? "↑ rising" : curr < prev ? "↓ falling" : "stable";
  return { value: `+${curr.toFixed(1)}°`, trend: dir };
}

function Hero() {
  const skin = useSkinTempTicker();
  return (
    <section
      id="top"
      className="relative overflow-hidden"
      style={{ background: TOKENS.ivory }}
    >
      <div className="mx-auto max-w-7xl px-5 pt-32 pb-20 sm:px-8 sm:pt-40 lg:pt-44 lg:pb-32">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_1fr] lg:gap-20">
          <ScrollReveal>
            <div className="max-w-xl">
              <div
                className="mb-7 text-[11px] uppercase tracking-[0.28em]"
                style={{ color: TOKENS.muted }}
              >
                Menopause Intelligence
              </div>
              <h1
                style={{
                  fontFamily: SERIF,
                  fontSize: "clamp(44px, 7.4vw, 84px)",
                  lineHeight: 0.98,
                  letterSpacing: "-0.03em",
                  color: TOKENS.charcoal,
                  fontWeight: 600,
                }}
              >
                Your menopause data,<br />
                <em style={{ fontStyle: "italic", color: TOKENS.clay, fontFamily: SERIF_ED, fontWeight: 500 }}>
                  finally understood.
                </em>
              </h1>
              <p
                className="mt-7 max-w-lg text-[17px] leading-[1.55]"
                style={{ color: TOKENS.muted }}
              >
                KnowMeno turns symptoms and daily patterns into clear
                insights women can understand — and doctors can actually use.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Link
                  to="/sign-up"
                  className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-[14px] font-medium transition-transform hover:scale-[1.02]"
                  style={{ background: TOKENS.charcoal, color: TOKENS.ivory }}
                >
                  Join the Waitlist
                  <ArrowRight size={16} />
                </Link>
                <a
                  href="#how"
                  className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-[14px] font-medium transition-colors"
                  style={{
                    border: `1px solid ${TOKENS.charcoal}`,
                    color: TOKENS.charcoal,
                  }}
                >
                  See How It Works
                </a>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delayMs={120}>
            <div className="relative">
              <div
                className="relative mx-auto overflow-hidden"
                style={{
                  borderRadius: 32,
                  aspectRatio: "4 / 5",
                  maxWidth: 540,
                  boxShadow: "0 40px 80px -40px rgba(31,31,28,0.35)",
                }}
              >
                <picture>
                  <source type="image/avif" srcSet={HERO_AVIF_SRCSET} sizes={HERO_SIZES} />
                  <source type="image/webp" srcSet={HERO_WEBP_SRCSET} sizes={HERO_SIZES} />
                  <img
                    src={HERO_JPG_FALLBACK}
                    srcSet={HERO_JPG_SRCSET}
                    sizes={HERO_SIZES}
                    alt="A woman in her fifties in soft natural light"
                    width={1280}
                    height={1600}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </picture>
              </div>

              {/* Floating metric cards */}
              <FloatingCard
                size="lg"
                className="km-float"
                style={{ top: "2%", right: "-8%" }}
                label="Menopause Score"
                value="73"
                trend="Moderate impact"
                accent={TOKENS.blush}
                icon={<Gauge size={18} />}
              />
              <FloatingCard
                className="km-float km-float-delay-1"
                style={{ top: "6%", left: "-6%" }}
                label="Sleep Score"
                value="84"
                trend="+6"
                accent={TOKENS.sage}
                icon={<Moon size={16} />}
              />
              <FloatingCard
                className="km-float km-float-delay-2"
                style={{ top: "38%", right: "-10%" }}
                label="Today's Outlook"
                value="Medium"
                trend="hot-flash risk"
                accent={TOKENS.blush}
                icon={<Sparkles size={16} />}
              />
              <FloatingCard
                className="km-float km-float-delay-3"
                style={{ top: "52%", left: "-8%" }}
                label="Fitness Age"
                value="47"
                unit="yrs"
                trend="−3 vs actual"
                accent={TOKENS.sage}
                icon={<Dumbbell size={16} />}
              />
              <FloatingCard
                className="km-float km-float-delay-4"
                style={{ bottom: "18%", right: "-6%" }}
                label="HRV"
                value="48"
                unit="ms"
                trend="+3"
                accent={TOKENS.data}
                icon={<HeartPulse size={16} />}
              />
              <FloatingCard
                className="km-float km-float-delay-5"
                style={{ bottom: "5%", left: "-5%" }}
                label="Skin Temp"
                value={skin.value}
                trend={skin.trend}
                accent={TOKENS.clay}
                icon={<Thermometer size={16} />}
              />
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}

function FloatingCard({
  label,
  value,
  unit,
  trend,
  accent,
  icon,
  style,
  className,
  size = "md",
}: {
  label: string;
  value: string;
  unit?: string;
  trend?: string;
  accent: string;
  icon: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  size?: "md" | "lg";
}) {
  const isLg = size === "lg";
  return (
    <div
      className={`absolute hidden sm:block ${className ?? ""}`}
      style={{
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(14px) saturate(140%)",
        border: "1px solid rgba(31,31,28,0.06)",
        borderRadius: isLg ? 24 : 20,
        padding: isLg ? "18px 22px" : "14px 18px",
        minWidth: isLg ? 210 : 168,
        boxShadow: isLg
          ? "0 28px 56px -24px rgba(31,31,28,0.36)"
          : "0 18px 40px -22px rgba(31,31,28,0.28)",
        ...style,
      }}
    >
      <div className="flex items-center gap-2" style={{ color: TOKENS.muted }}>
        <span
          className={`grid place-items-center rounded-full ${isLg ? "h-7 w-7" : "h-6 w-6"}`}
          style={{ background: accent, color: TOKENS.charcoal }}
        >
          {icon}
        </span>
        <span className="text-[11px] uppercase tracking-[0.16em]">{label}</span>
      </div>
      <div
        className="mt-2 flex items-baseline gap-1"
        style={{ fontFamily: SERIF, color: TOKENS.charcoal }}
      >
        <span style={{ fontSize: isLg ? 38 : 28, lineHeight: 1, letterSpacing: "-0.02em" }}>
          {value}
        </span>
        {unit && (
          <span style={{ fontSize: 13, color: TOKENS.muted, fontFamily: UI }}>
            {unit}
          </span>
        )}
      </div>
      {trend && (
        <div className="mt-1 text-[11px]" style={{ color: TOKENS.muted }}>
          {trend}
        </div>
      )}
    </div>
  );
}

/* ---------------- 90-Day Journey ---------------- */

const JOURNEY_MILESTONES = [
  {
    day: "Day 1",
    title: "Today",
    essence: "Your Score + first insight, today",
    desc:
      "Your Menopause Vitality Score appears the first time you log — paired with a daily insight tied to what you entered. Free, from day one.",
  },
  {
    day: "Day 7",
    title: "Patterns emerge",
    essence: "Your symptom heatmap starts coloring in",
    desc:
      "A week of logs is enough to see when your symptoms cluster. Your heatmap fills in — your first real look at your own patterns.",
  },
  {
    day: "Day 14",
    title: "Clinically meaningful",
    essence: "You can see everything. Plus tells you what it means.",
    desc:
      "Your Doctor Report fills with your real data — symptom heatmap, hot-flash timing, HRT response, labs, cycle history. It's all yours, free. But data isn't answers. Plus reads it for you: what's getting worse, what's triggering it, what to tell your doctor — ready to share in one tap.",
  },
  {
    day: "Day 30",
    title: "The fuller picture",
    essence: "A richer month of patterns, side by side",
    desc:
      "Your hormone therapy and symptoms sit side by side over time — mood trends, cycle patterns, and your triggers emerging. The longer you log, the clearer the picture gets.",
  },
  {
    day: "Day 90",
    title: "The full picture",
    essence: "Long-term trends, HRT tracking, cycle patterns",
    desc:
      "Ninety days reveals the strongest signal — long-term trends, your hormone therapy and lifestyle changes tracked alongside symptoms, and the full picture across your cycle.",
  },
];

function NinetyDayJourney() {
  return (
    <section className="px-5 py-24 sm:px-8 sm:py-32" style={{ background: TOKENS.ivory }}>
      <div className="mx-auto max-w-7xl">
        <ScrollReveal>
          <div className="max-w-2xl">
            <div
              className="mb-5 text-[11px] uppercase tracking-[0.28em]"
              style={{ color: TOKENS.muted }}
            >
              Your 90-day journey
            </div>
            <h2
              style={{
                fontFamily: SERIF,
                fontSize: "clamp(32px, 4.6vw, 56px)",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                color: TOKENS.charcoal,
              }}
            >
              Useful from Day 1. Powerful by Day 90.
            </h2>
            <p className="mt-5 max-w-xl text-[16px] leading-relaxed" style={{ color: TOKENS.muted }}>
              You don't wait for value. Here's what unlocks as your pattern data grows.
            </p>
          </div>
        </ScrollReveal>

        <ol className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {JOURNEY_MILESTONES.map((m, i) => (
            <li key={m.day}>
              <ScrollReveal delayMs={i * 80}>
                <div
                  className="km-lift h-full p-6 sm:p-7"
                  style={{
                    background: TOKENS.white,
                    borderRadius: 22,
                    border: `1px solid ${TOKENS.sand}55`,
                  }}
                >
                  <div
                    className="text-[12px] font-semibold uppercase tracking-[0.18em]"
                    style={{ color: TOKENS.blush }}
                    aria-label={`Step ${i + 1} of ${JOURNEY_MILESTONES.length}: ${m.day}`}
                  >
                    {m.day}
                  </div>
                  <h3
                    className="mt-3"
                    style={{
                      fontFamily: SERIF,
                      fontSize: 22,
                      letterSpacing: "-0.015em",
                      color: TOKENS.charcoal,
                      lineHeight: 1.15,
                    }}
                  >
                    {m.title}
                  </h3>
                  <p
                    className="mt-2 text-[14px] font-medium leading-snug"
                    style={{ color: TOKENS.charcoal }}
                  >
                    {m.essence}
                  </p>
                  <p className="mt-3 text-[14px] leading-relaxed" style={{ color: TOKENS.muted }}>
                    {m.desc}
                  </p>
                </div>
              </ScrollReveal>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ---------------- Postmenopause ---------------- */

function PostmenoSection() {
  const cards = [
    {
      title: "Lab & hormone trends",
      body:
        "Upload your results and KnowMeno tracks your hormone and thyroid markers against reference ranges, so trends are easy to bring to your doctor.",
    },
    {
      title: "Preventive screenings, on track",
      body:
        "Mammogram, bone density, colonoscopy, and more — KnowMeno keeps your screening timeline in one place so nothing slips.",
    },
    {
      title: "Your symptoms still tell a story",
      body:
        "Menopause doesn't end your symptoms — and what you feel still signals what's changing in your body. Brain fog, mood, joint pain, sleep, hot flashes: log them in seconds and watch your 90-day trends and direction, so you and your doctor can see what's shifting and act on it.",
    },
  ];
  return (
    <section className="px-5 py-24 sm:px-8 sm:py-32" style={{ background: TOKENS.ivory }}>
      <div className="mx-auto max-w-7xl">
        <ScrollReveal>
          <div className="max-w-2xl">
            <div
              className="mb-5 text-[11px] uppercase tracking-[0.28em]"
              style={{ color: TOKENS.muted }}
            >
              POSTMENOPAUSE + LONGEVITY
            </div>
            <h2
              style={{
                fontFamily: SERIF,
                fontSize: "clamp(32px, 4.6vw, 56px)",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                color: TOKENS.charcoal,
              }}
            >
              Vitality and longevity, after menopause.
            </h2>
            <p className="mt-5 max-w-md text-[16px] leading-relaxed" style={{ color: TOKENS.muted }}>
              Postmenopause is about longevity — knowing your body and protecting your health for the decades ahead. Your Vitality Score and 90-day report bring together labs, screenings, and symptoms so you can see how you're doing and where to focus.
            </p>
          </div>
        </ScrollReveal>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((c, i) => (
            <ScrollReveal key={c.title} delayMs={i * 80}>
              <div
                className="km-lift h-full p-6"
                style={{
                  background: TOKENS.white,
                  borderRadius: 22,
                  border: `1px solid ${TOKENS.sand}55`,
                }}
              >
                <div
                  className="mt-1"
                  style={{
                    fontFamily: SERIF,
                    fontSize: 22,
                    letterSpacing: "-0.015em",
                    color: TOKENS.charcoal,
                    lineHeight: 1.15,
                  }}
                >
                  {c.title}
                </div>
                <p className="mt-3 text-[13.5px] leading-relaxed" style={{ color: TOKENS.muted }}>
                  {c.body}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <p className="mt-10 max-w-2xl text-[13.5px] leading-relaxed" style={{ color: TOKENS.muted }}>
          Wearable sync is coming soon — recovery and cardio-fitness insights will appear here once you connect. Your full report is free
          — Plus adds one-tap sharing with your doctor.
        </p>
      </div>
    </section>
  );
}

/* ---------------- Outlook ---------------- */

function OutlookSection() {
  return (
    <section
      id="insights"
      className="px-5 py-24 sm:px-8 sm:py-32"
      style={{ background: TOKENS.sand + "55" }}
    >
      <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-2 lg:gap-20">
        <ScrollReveal>
          <div>
            <div
              className="mb-5 text-[11px] uppercase tracking-[0.28em]"
              style={{ color: TOKENS.muted }}
            >
              Daily outlook
            </div>
            <h2
              style={{
                fontFamily: SERIF,
                fontSize: "clamp(32px, 4.6vw, 56px)",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                color: TOKENS.charcoal,
              }}
            >
              Today, at a glance.
            </h2>
            <p className="mt-5 max-w-md text-[16px] leading-relaxed" style={{ color: TOKENS.muted }}>
              A single calm read each morning — whether today is a Low, Medium, or
              High symptom-risk day, and what your body is signalling underneath.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delayMs={120}>
          <DailyOutlookCard />
        </ScrollReveal>
      </div>
    </section>
  );
}

function DailyOutlookCard() {
  const levels = [
    { label: "Low", active: false, color: TOKENS.sage },
    { label: "Medium", active: true, color: TOKENS.clay },
    { label: "High", active: false, color: TOKENS.taupe },
  ];
  return (
    <div
      className="km-lift mx-auto w-full max-w-md p-8"
      style={{
        background: TOKENS.white,
        borderRadius: 28,
        boxShadow: "0 30px 60px -34px rgba(31,31,28,0.25)",
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.2em]" style={{ color: TOKENS.muted }}>
          Today, Tuesday
        </span>
        <span className="text-[11px]" style={{ color: TOKENS.muted }}>
          v. yesterday +2
        </span>
      </div>
      <div
        className="mt-5"
        style={{
          fontFamily: SERIF,
          fontSize: 72,
          lineHeight: 1,
          letterSpacing: "-0.03em",
          color: TOKENS.charcoal,
        }}
      >
        Medium
      </div>
      <div className="mt-1 text-[14px]" style={{ color: TOKENS.muted }}>
        Hot-flash risk slightly elevated tonight.
      </div>

      <div className="mt-7 grid grid-cols-3 gap-2">
        {levels.map((l) => (
          <div
            key={l.label}
            className="rounded-2xl px-3 py-3 text-center"
            style={{
              background: l.active ? l.color : TOKENS.ivory,
              color: l.active ? TOKENS.charcoal : TOKENS.muted,
              border: `1px solid ${l.active ? "transparent" : TOKENS.sand}`,
              fontWeight: l.active ? 600 : 400,
              fontSize: 13,
            }}
          >
            {l.label}
          </div>
        ))}
      </div>

      <div className="mt-7 space-y-3">
        {[
          { k: "Skin temp", v: "+0.4° vs baseline" },
          { k: "HRV", v: "48 ms · in range" },
          { k: "Sleep", v: "7h 12m · 2 wake-ups" },
        ].map((row) => (
          <div
            key={row.k}
            className="flex items-center justify-between border-t pt-3 text-[13.5px]"
            style={{ borderColor: TOKENS.sand + "88", color: TOKENS.charcoal }}
          >
            <span style={{ color: TOKENS.muted }}>{row.k}</span>
            <span>{row.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Stages ---------------- */

function StagesSection() {
  const stages = [
    {
      key: "peri" as const,
      title: "Perimenopause",
      body: "Cycles shift. Sleep changes. Patterns start emerging.",
      sources: stageSources.peri,
    },
    {
      key: "meno" as const,
      title: "Menopause",
      body: "Twelve months in. Your body is recalibrating — and worth paying attention to.",
      sources: stageSources.meno,
    },
    {
      key: "post" as const,
      title: "Postmenopause",
      body: "Long-term metrics that matter: bone, heart, sleep, energy.",
      sources: stageSources.post,
    },
  ];
  return (
    <section className="px-5 py-24 sm:px-8 sm:py-32" style={{ background: TOKENS.ivory }}>
      <div className="mx-auto max-w-7xl">
        <ScrollReveal>
          <div className="max-w-2xl">
            <div
              className="mb-5 text-[11px] uppercase tracking-[0.28em]"
              style={{ color: TOKENS.muted }}
            >
              For every stage
            </div>
            <h2
              style={{
                fontFamily: SERIF,
                fontSize: "clamp(32px, 4.6vw, 56px)",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                color: TOKENS.charcoal,
              }}
            >
              Built for women 40+.
            </h2>
          </div>
        </ScrollReveal>

        <div className="mt-12 grid gap-6 sm:gap-8 md:grid-cols-3">
          {stages.map((s, i) => {
            const href =
              s.key === "meno"
                ? "/menopause"
                : s.key === "peri"
                  ? "/perimenopause"
                  : s.key === "post"
                    ? "/postmenopause"
                    : undefined;
            const inner = (
              <article
                className="km-lift overflow-hidden h-full flex flex-col"
                style={{
                  background: TOKENS.white,
                  borderRadius: 28,
                  border: `1px solid ${TOKENS.sand}55`,
                  cursor: href ? "pointer" : "default",
                }}
              >
                <div style={{ aspectRatio: "4 / 5", overflow: "hidden" }}>
                  <picture>
                    <source type="image/avif" srcSet={s.sources.avif} sizes={STAGE_SIZES} />
                    <source type="image/webp" srcSet={s.sources.webp} sizes={STAGE_SIZES} />
                    <img
                      src={s.sources.fallback}
                      srcSet={s.sources.jpg}
                      sizes={STAGE_SIZES}
                      alt={s.title}
                      loading="lazy"
                      decoding="async"
                      width={800}
                      height={1000}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center 8%",
                        display: "block",
                      }}
                    />
                  </picture>
                </div>
                <div className="p-6 sm:p-7">
                  <h3
                    style={{
                      fontFamily: SERIF,
                      fontSize: "clamp(22px, 2.4vw, 28px)",
                      letterSpacing: "-0.02em",
                      color: TOKENS.charcoal,
                      fontWeight: 600,
                    }}
                  >
                    {s.title}
                  </h3>
                  <p
                    className="mt-3 text-[15px] leading-relaxed"
                    style={{ color: TOKENS.charcoal, fontWeight: 500 }}
                  >
                    {s.body}
                  </p>
                </div>
              </article>
            );
            return (
              <ScrollReveal key={s.key} delayMs={i * 80}>
                {href ? (
                  <a href={href} style={{ textDecoration: "none", color: "inherit", display: "block", height: "100%" }} aria-label={`Learn about ${s.title}`}>
                    {inner}
                  </a>
                ) : (
                  inner
                )}
              </ScrollReveal>
            );
          })}

        </div>
      </div>
    </section>
  );
}

/* ---------------- Patterns ---------------- */

/* ---------------- Patterns ---------------- */

function PatternsSection() {
  const logs = [
    { day: "Mon", flag: "Hot flash · 2:14 AM", color: TOKENS.clay },
    { day: "Tue", flag: "Sleep 7h 12m · 2 wakes", color: TOKENS.sage },
    { day: "Wed", flag: "Brain fog · evening", color: TOKENS.taupe },
    { day: "Thu", flag: "HRV +6 · best in 9 days", color: TOKENS.sage },
    { day: "Fri", flag: "Hot flash · pre-period", color: TOKENS.clay },
  ];
  return (
    <section
      className="px-5 py-24 sm:px-8 sm:py-32"
      style={{ background: TOKENS.softBlack, color: TOKENS.ivory }}
    >
      <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-2 lg:gap-20">
        <ScrollReveal>
          <div>
            <div
              className="mb-5 text-[11px] uppercase tracking-[0.28em]"
              style={{ color: TOKENS.data }}
            >
              Symptoms → patterns
            </div>
            <h2
              style={{
                fontFamily: SERIF,
                fontSize: "clamp(32px, 4.6vw, 56px)",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                color: TOKENS.ivory,
              }}
            >
              Five seconds a day becomes a clear picture in two weeks.
            </h2>
            <p className="mt-5 max-w-md text-[16px] leading-relaxed" style={{ color: TOKENS.taupe }}>
              You log how you feel. KnowMeno will cross-reference it with your wearable
              data once connected — surfacing patterns you'd never spot on your own.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delayMs={120}>
          <div
            className="p-7"
            style={{
              background: "rgba(246,241,232,0.04)",
              borderRadius: 28,
              border: "1px solid rgba(246,241,232,0.08)",
            }}
          >
            <div className="text-[11px] uppercase tracking-[0.22em]" style={{ color: TOKENS.data }}>
              This week
            </div>
            <ul className="mt-5 divide-y" style={{ borderColor: "rgba(246,241,232,0.08)" }}>
              {logs.map((l) => (
                <li
                  key={l.day}
                  className="flex items-center justify-between py-4"
                  style={{ borderTopColor: "rgba(246,241,232,0.08)" }}
                >
                  <div className="flex items-center gap-4">
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: l.color,
                        display: "inline-block",
                      }}
                    />
                    <span style={{ fontFamily: SERIF, fontSize: 22, color: TOKENS.ivory }}>
                      {l.day}
                    </span>
                  </div>
                  <span className="text-[13.5px]" style={{ color: TOKENS.taupe }}>
                    {l.flag}
                  </span>
                </li>
              ))}
            </ul>
            <div
              className="mt-6 rounded-2xl p-5"
              style={{
                background: "rgba(169,184,162,0.12)",
                border: "1px solid rgba(169,184,162,0.25)",
              }}
            >
              <div className="text-[11px] uppercase tracking-[0.2em]" style={{ color: TOKENS.sage }}>
                Pattern detected
              </div>
              <div className="mt-2 text-[15px]" style={{ color: TOKENS.ivory }}>
                Hot flashes cluster 2–4 nights before your period and align with a
                +0.5° skin-temp rise.
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ---------------- Doctor ---------------- */

function DoctorSection() {
  return (
    <section
      id="doctors"
      className="px-5 py-24 sm:px-8 sm:py-32"
      style={{ background: TOKENS.ivory }}
    >
      <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[1fr_1.05fr] lg:gap-20">
        <ScrollReveal>
          <div
            className="km-lift p-8"
            style={{
              background: TOKENS.white,
              borderRadius: 28,
              border: `1px solid ${TOKENS.sand}88`,
            }}
          >
            <div className="flex items-center justify-between">
              <div
                className="text-[10px] uppercase tracking-[0.24em]"
                style={{ color: TOKENS.muted }}
              >
                Symptom Intelligence Report
              </div>
              <FileText size={16} color={TOKENS.muted} />
            </div>
            <div
              className="mt-4"
              style={{
                fontFamily: SERIF,
                fontSize: 30,
                letterSpacing: "-0.02em",
                color: TOKENS.charcoal,
                lineHeight: 1.1,
              }}
            >
              Patient: J. M. · 51 · 90-day summary
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { k: "Sleep", v: "6h 42m", sub: "−18% deep" },
                { k: "HRV", v: "41 ms", sub: "trend ↓" },
                { k: "Hot flashes", v: "5.2/wk", sub: "luteal" },
              ].map((m) => (
                <div
                  key={m.k}
                  className="rounded-2xl p-4"
                  style={{ background: TOKENS.ivory, color: TOKENS.charcoal }}
                >
                  <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: TOKENS.muted }}>
                    {m.k}
                  </div>
                  <div
                    className="mt-1"
                    style={{ fontFamily: SERIF, fontSize: 22, letterSpacing: "-0.015em" }}
                  >
                    {m.v}
                  </div>
                  <div className="text-[11px]" style={{ color: TOKENS.muted }}>
                    {m.sub}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-3 text-[13.5px]" style={{ color: TOKENS.charcoal }}>
              {[
                "90-day symptom log, organized by cycle phase",
                "Wearable trends shown alongside logged symptoms",
                "Likely stage: late perimenopause",
              ].map((row) => (
                <div key={row} className="flex items-start gap-2">
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: TOKENS.clay,
                      display: "inline-block",
                      marginTop: 7,
                    }}
                  />
                  <span>{row}</span>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delayMs={120}>
          <div>
            <div
              className="mb-5 text-[11px] uppercase tracking-[0.28em]"
              style={{ color: TOKENS.muted }}
            >
              For your doctor
            </div>
            <h2
              style={{
                fontFamily: SERIF,
                fontSize: "clamp(32px, 4.6vw, 56px)",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                color: TOKENS.charcoal,
              }}
            >
              Bring better data, not louder words.
            </h2>
            <p className="mt-5 max-w-md text-[16px] leading-relaxed" style={{ color: TOKENS.muted }}>
              A clean, structured PDF your provider can read in under a minute —
              so the conversation starts with your tracked data, not "are you sure?"
            </p>
            <Link
              to="/sign-up"
              className="mt-9 inline-flex items-center gap-2 rounded-full px-7 py-4 text-[14px] font-medium transition-transform hover:scale-[1.02]"
              style={{ background: TOKENS.charcoal, color: TOKENS.ivory }}
            >
              Join the Waitlist <ArrowRight size={16} />
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ---------------- Wearables ---------------- */

function WearablesSection() {
  const sources = [
    "Oura",
    "Fitbit",
    "Garmin",
    "Whoop",
    "Withings",
    "Apple Watch",
    "Health Connect",
  ];
  return (
    <section
      className="px-5 py-24 sm:px-8 sm:py-32"
      style={{ background: TOKENS.sand + "44" }}
    >
      <div className="mx-auto max-w-5xl text-center">
        <ScrollReveal>
          <div
            className="mb-5 text-[11px] uppercase tracking-[0.28em]"
            style={{ color: TOKENS.muted }}
          >
            Wearable intelligence — coming soon
          </div>
          <h2
            style={{
              fontFamily: SERIF,
              fontSize: "clamp(32px, 4.6vw, 52px)",
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              color: TOKENS.charcoal,
            }}
          >
            Built for the devices you already wear.
          </h2>
        </ScrollReveal>

        <ScrollReveal delayMs={100}>
          <div
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
            style={{ opacity: 0.75 }}
            aria-label="Wearable integrations coming soon"
          >
            <span
              className="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]"
              style={{
                background: TOKENS.white,
                color: TOKENS.muted,
                border: `1px dashed ${TOKENS.sand}`,
              }}
            >
              Coming soon
            </span>
            {sources.map((src) => (
              <span
                key={src}
                className="rounded-full px-5 py-2.5 text-[13px]"
                style={{
                  background: "transparent",
                  color: TOKENS.muted,
                  border: `1px dashed ${TOKENS.sand}`,
                }}
              >
                {src}
              </span>
            ))}
          </div>
          <p
            className="mx-auto mt-6 max-w-2xl text-[13px] leading-[1.6]"
            style={{ color: TOKENS.muted }}
          >
            Wearable sync is coming soon. We're finishing integration with Oura,
            Fitbit, Garmin, Whoop, and Withings — with Apple Watch and Android
            Health Connect to follow in our mobile app.
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ---------------- Trust ---------------- */

function TrustSection() {
  const items = [
    { icon: <Lock size={18} />, title: "Privacy first", body: "Your data is yours. Encrypted at rest and in transit." },
    { icon: <Eye size={18} />, title: "Full data control", body: "Export, delete, or pause your data anytime." },
    { icon: <FileText size={18} />, title: "Doctor-ready", body: "A clinical-grade report your provider can act on." },
    { icon: <ShieldCheck size={18} />, title: "No guessing", body: "Insights are grounded in your patterns, not generic averages." },
  ];
  return (
    <section
      id="pricing"
      className="px-5 py-24 sm:px-8 sm:py-32"
      style={{ background: TOKENS.ivory }}
    >
      <div className="mx-auto max-w-7xl">
        <ScrollReveal>
          <div className="max-w-2xl">
            <div
              className="mb-5 text-[11px] uppercase tracking-[0.28em]"
              style={{ color: TOKENS.muted }}
            >
              Designed for trust
            </div>
            <h2
              style={{
                fontFamily: SERIF,
                fontSize: "clamp(32px, 4.6vw, 56px)",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                color: TOKENS.charcoal,
              }}
            >
              Quiet, careful, and built around your privacy.
            </h2>
          </div>
        </ScrollReveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it, i) => (
            <ScrollReveal key={it.title} delayMs={i * 80}>
              <div
                className="km-lift h-full p-7"
                style={{
                  background: TOKENS.white,
                  borderRadius: 24,
                  border: `1px solid ${TOKENS.sand}55`,
                }}
              >
                <span
                  className="grid h-10 w-10 place-items-center rounded-full"
                  style={{ background: TOKENS.ivory, color: TOKENS.charcoal }}
                >
                  {it.icon}
                </span>
                <h3
                  className="mt-5"
                  style={{
                    fontFamily: SERIF,
                    fontSize: 22,
                    letterSpacing: "-0.015em",
                    color: TOKENS.charcoal,
                  }}
                >
                  {it.title}
                </h3>
                <p className="mt-2 text-[14px] leading-relaxed" style={{ color: TOKENS.muted }}>
                  {it.body}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Final CTA ---------------- */

function FinalCTA() {
  return (
    <section
      className="px-5 py-28 sm:px-8 sm:py-40"
      style={{ background: TOKENS.charcoal, color: TOKENS.ivory }}
    >
      <div className="mx-auto max-w-4xl text-center">
        <ScrollReveal>
          <h2
            style={{
              fontFamily: SERIF,
              fontSize: "clamp(44px, 7vw, 96px)",
              lineHeight: 0.98,
              letterSpacing: "-0.03em",
              color: TOKENS.ivory,
            }}
          >
            Stop guessing.<br />
            <em style={{ fontStyle: "italic", color: TOKENS.clay }}>Know Meno.</em>
          </h2>
          <p
            className="mx-auto mt-7 max-w-lg text-[16px] leading-relaxed"
            style={{ color: TOKENS.taupe }}
          >
            Join the waitlist and be among the first women to bring real data — not
            apologies — to the menopause conversation.
          </p>
          <Link
            to="/sign-up"
            className="mt-10 inline-flex items-center gap-2 rounded-full px-8 py-4 text-[14px] font-medium transition-transform hover:scale-[1.02]"
            style={{ background: TOKENS.ivory, color: TOKENS.charcoal }}
          >
            Join the Waitlist <ArrowRight size={16} />
          </Link>
        </ScrollReveal>
      </div>
    </section>
  );
}

function _unused() {
  return <PrevAssetsRef />;
}
function PrevAssetsRef() {
  // Kept to avoid TS unused-import noise during refactors. No-op.
  return null;
}
